import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES, Reveal } from "@/components/site/shared";
import { Marquee } from "@/components/site/Marquee";
import { Countdown } from "@/components/site/Countdown";

const line = {
  hidden: { opacity: 0, y: "110%" },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};

const HERO_LINES = ["Dan", "& Maggie"];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.03]);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative h-[85svh] w-full overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y, scale }} className="absolute inset-0 border-r-[6px] md:border-r-8 border-ink">
          <img
            src={IMAGES.hero}
            alt="Dan and Maggie keepsakes"
            className="w-full h-full object-cover object-center opacity-[0.95]"
          />
          <div className="absolute inset-0 bg-black/35" />
        </motion.div>

        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-8 flex flex-col justify-start pt-28 md:pt-40">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="overline text-yellow mb-5"
          >
            We're getting married
          </motion.p>
          <h1 className="font-display text-cream leading-[1.05] pb-2 text-6xl sm:text-7xl md:text-8xl lg:text-9xl">
            {HERO_LINES.map((t, i) => (
              <span key={t} className="block overflow-hidden pb-[0.12em]">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                  {t}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <span className="font-grotesk font-bold text-cream text-xl md:text-2xl">May 22, 2027 · Philadelphia, PA</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-cream/80 text-xs tracking-[0.3em] uppercase"
        >
          Scroll ↓
        </motion.div>
      </section>

      <Marquee />

      <Countdown />

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px- md:px-10 py-24 md:py-16 grid md:grid-cols-12 gap-10 items-start">
        <Reveal className="md:col-span-3">
          <p className="overline text-orange">The Invitation</p>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-9">
          <h2 className="font-grotesk font-extrabold text-3xl sm:text-4xl md:text-5xl leading-tight text-ink tracking-tight">
            A Pennsylvania party with vintage feels. 
          </h2>
        </Reveal>
      </section>

      {/* QUICK FACTS BENTO */}
      <section className="max-w-7x1 mx-auto px- md:px-8 pb-24 md:pb-12">
        <div className="grid grid-cols-1 gap-5">
          {[
            { k: "The Date", v: "May 22, 2027", sub: "Saturday · 4:30 PM", bg: "bg-pink" },
            { k: "The Place", v: "Cannstatter Volksfest-Verein", sub: "Philadelphia, PA", bg: "bg-blueglass" },
            { k: "The Vibe", v: "A little retro", sub: "A banquet hall party with vintage charm", bg: "bg-yellow" },
          ].map((f, i) => (
            <Reveal key={f.k} delay={i * 0.12}>
              <div className={`card-retro ${f.bg} p-8 h-full transition-transform duration-200 hover:-translate-y-1`}>
                <p className="overline text-ink/70">{f.k}</p>
                <p className="font-display text-3xl md:text-4xl text-ink mt-4">{f.v}</p>
                <p className="font-body text-ink/80 mt-2">{f.sub}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HALF PAGE PHOTO */}
      <section className="grid md:grid-cols-2">
        <div className="bg-red text-cream p-10 md:p-20 flex flex-col justify-center">
          <Reveal>
            <p className="overline text-yellow">The Weekend</p>
            <h3 className="font-display text-4xl md:text-6xl mt-5 leading-tight">Save our Date</h3>
            <p className="font-body text-cream/85 mt-6 max-w-md text-lg">
              Join us for a welcome party on Friday, May 21st, followed by our ceremony on Saturday, May 22nd.
              A formal invitation with all the details will follow.
            </p>
          </Reveal>
        </div>
        <div className="h-[60vh] md:h-auto overflow-hidden">
          <img src={IMAGES.disco} alt="Disco balls" className="w-full h-full object-cover" />
        </div>
      </section>
    </div>
  );
}
